import { useRef, useLayoutEffect, useState } from 'react';
import { selectIncidentCommunicationEnabled } from 'core/config/firebase/remote-config-slice';
import { MaintenanceEnum } from 'core/models/maintenance.model';
import { useAppSelector } from './use-app-selector';
import { useAppStatusConfig } from './use-app-status-config';

type Props = {
  statusMode: MaintenanceEnum;
};

const useMaintenanceBanner = ({ statusMode }: Props) => {
  const [isVisible, setIsVisible] = useState(true);
  const appStatusConfig = useAppStatusConfig();
  const incidentCommunicationEnabled = useAppSelector(selectIncidentCommunicationEnabled);
  const isMatchingStatusMode = appStatusConfig.statusMode === statusMode;
  const showBanner = isMatchingStatusMode && isVisible;

  const shouldRender = incidentCommunicationEnabled && isMatchingStatusMode;

  const bannerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!shouldRender) {
      return undefined;
    }
    const el = bannerRef.current;
    if (!el) {
      document.documentElement.style.setProperty('--maintenance-banner-height', '0px');
      return undefined;
    }
    const update = () => {
      document.documentElement.style.setProperty(
        '--maintenance-banner-height',
        showBanner ? `${el.offsetHeight}px` : '0px'
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--maintenance-banner-height', '0px');
    };
  }, [showBanner, shouldRender]);

  return { shouldRender, showBanner, appStatusConfig, isMatchingStatusMode, bannerRef, setIsVisible };
};

export default useMaintenanceBanner;
